import { useState } from 'react';

import { FaPlus, FaTrash } from 'react-icons/fa';
import styled from 'styled-components';

import { useFieldArray } from 'react-hook-form';

import { GetSitesQuery } from 'data/__generated__';

import { colors } from 'constants/colors';

import { DetailValue } from 'components/DetailsView';
import { TotemSelect } from 'components/TotemSelect';

import { UserDetailsFormData } from 'pages/Users/UserDetails/UserDetails';

type Props = {
    sites: GetSitesQuery['sites'];
};

export const VisitedSitesSection = ({ sites }: Props) => {
    const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

    const { fields, append, remove } = useFieldArray<UserDetailsFormData, 'visitedSites'>({
        name: 'visitedSites',
    });

    const visitedSiteIds = new Set(fields.map((f) => f._id));
    const sitesOptions = sites
        .filter(({ _id }) => !visitedSiteIds.has(_id))
        .map(({ _id, name }) => ({ value: _id, label: name }));

    return (
        <Container>
            <List>
                {fields.length === 0 ? <EmptyHint>Aucun site visité.</EmptyHint> : null}
                {fields.map((site, index) => (
                    <Row key={site.id}>
                        <DetailValue value={site.name || 'inconnu'} />
                        <IconContainer onClick={() => remove(index)}>
                            <TrashIcon data-test="trash-icon" size={16} color={colors.pureWhite} />
                        </IconContainer>
                    </Row>
                ))}
            </List>
            <AddRow>
                <TotemSelect
                    label="Ajouter un site"
                    placeholder="Sélectionner un site (recherche par nom ou par _id)"
                    value={selectedSiteId}
                    options={sitesOptions}
                    onChange={(siteId) => setSelectedSiteId(siteId)}
                />
                {selectedSiteId ? (
                    <IconContainer
                        onClick={() => {
                            const site = sitesOptions.find((o) => o.value === selectedSiteId);
                            if (site) {
                                append({
                                    _id: site.value,
                                    name: site.label,
                                    visitedAt: Date.now(),
                                });
                            }
                            setSelectedSiteId(null);
                        }}
                    >
                        <PlusIcon color={colors.pureWhite} />
                    </IconContainer>
                ) : null}
            </AddRow>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 220px;
    overflow-y: auto;
    padding-right: 4px;
`;

const EmptyHint = styled.div`
    color: ${({ theme }) => theme.textColor};
    opacity: 0.6;
    font-size: 13px;
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: var(--radius-md);
`;

const AddRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    gap: 10px;
`;

const TrashIcon = styled(FaTrash)`
    color: ${({ theme }) => theme.menuTextColor};
`;

const PlusIcon = styled(FaPlus)`
    color: ${({ theme }) => theme.menuTextColor};
`;

const IconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 40px;
    background-color: ${({ theme }) => theme.ctaPrimaryColor};
    cursor: pointer;
    flex-shrink: 0;
`;
